import { useGetList } from "react-admin";
import { Card, CardContent, LinearProgress, Tooltip, Typography } from "@mui/material";
import { Column } from "./Column";
import { Fragment, ReactNode, useMemo, useState } from "react";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { ALL_CUSTOMIZATION_FLAGS, type CustomizationFlag } from "../model/customizationFlags";
import { l10n } from "../l10n";

type Stats = Record<CustomizationFlag, { value: number; tooltip1?: ReactNode; tooltip2?: ReactNode }>;

function useStats(drivingSchools?: Array<DrivingSchool>): Stats {
  return useMemo(() => {
    if (!drivingSchools || drivingSchools.length === 0) {
      return Object.fromEntries(ALL_CUSTOMIZATION_FLAGS.map((flag) => [flag, { value: 0 }])) as Stats;
    }
    return Object.fromEntries(
      ALL_CUSTOMIZATION_FLAGS.map((flag) => {
        const a1 = drivingSchools.filter((it) => it.customizations.includes(flag));
        const a2 = drivingSchools.filter((it) => !it.customizations.includes(flag));
        return [
          flag,
          {
            value: (a1.length / drivingSchools.length) * 100,
            tooltip1: (
              <p>
                {a1.map((it) => (
                  <Fragment key={it.id}>
                    {it.name}
                    <br />
                  </Fragment>
                ))}
              </p>
            ),
            tooltip2: (
              <p>
                {a2.map((it) => (
                  <Fragment key={it.id}>
                    {it.name}
                    <br />
                  </Fragment>
                ))}
              </p>
            ),
          },
        ];
      }),
    );
  }, [drivingSchools?.length]) as Stats;
}

export function CustomizationsFlagsUsage() {
  const { data: drivingSchools } = useGetList("drivingSchools", {
    filter: { isActive: true },
    pagination: { page: 1, perPage: 999 },
  });
  const stats = useStats(drivingSchools);
  const [mouseOverCustomizationFlag, setMouseOverCustomizationFlag] = useState<undefined | CustomizationFlag>();
  const [mouseOverBar, setMouseOverBar] = useState<boolean>(false);

  return (
    <Card>
      <CardContent>
        <Column>
          <h4 style={{ margin: 0 }}>Customization Flags Usage</h4>
          {ALL_CUSTOMIZATION_FLAGS.map((customizationFlag) => {
            const description = l10n.customizationFlagLabel[customizationFlag];
            return (
              <div key={customizationFlag} style={{ marginTop: "10px" }}>
                <Typography variant="body1" style={{ whiteSpace: "nowrap" }}>
                  {description || customizationFlag}
                  {description && (
                    <Typography component="span" sx={{ color: "text.secondary" }}>
                      {`\u00A0(${customizationFlag})`}
                    </Typography>
                  )}
                </Typography>
                <Tooltip
                  title={mouseOverBar ? stats[customizationFlag].tooltip1 : stats[customizationFlag].tooltip2}
                  open={customizationFlag === mouseOverCustomizationFlag}
                  placement={mouseOverBar ? "bottom-start" : "bottom-end"}
                >
                  <LinearProgress
                    variant="determinate"
                    value={stats[customizationFlag].value}
                    style={{ height: "10px" }}
                    onMouseOver={(event) => {
                      setMouseOverCustomizationFlag(customizationFlag);
                      setMouseOverBar((event.target as HTMLElement).className.includes("MuiLinearProgress-bar"));
                    }}
                    onMouseOut={() => setMouseOverCustomizationFlag(undefined)}
                  />
                </Tooltip>
              </div>
            );
          })}
        </Column>
      </CardContent>
    </Card>
  );
}
